import { useEffect, useMemo, useRef, useState } from 'react';
import type { Class } from '../../data/types';
import { getPublishTheme } from '../themes';
import { resolveWidgetOrder } from '../widgetOrder';
import type { PublishConfig } from '../types';

/**
 * Sound Reactor template — full-bleed audio visualiser. The published
 * page captures the mic, runs the kid's CNN, and lights up the
 * matching class chip + paints a coloured pulse / bar wash on each
 * confident match. Built for "make a noise the model knows".
 *
 * The preview component below simulates the live mic by rotating
 * which class is "active" every ~1.4s so the composer pane shows
 * the page in action before the kid publishes.
 */

export interface SoundReactorProps {
  config: PublishConfig;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  liveLabelId?: string | null;
}

const SIM_TICK_MS = 1400;

export function SoundReactor({
  config,
  classes,
  liveLabelId = null,
}: SoundReactorProps): JSX.Element {
  const theme = getPublishTheme(config.themeId);
  const isWave = config.layoutVariant === 1;
  const ids = useMemo(() => classes.map((c) => c.id), [classes]);

  const [activeId, setActiveId] = useState<string | null>(liveLabelId ?? ids[0] ?? null);
  const [pulse, setPulse] = useState(false);

  // Preview-only: simulate confident matches by rotating the active
  // class every SIM_TICK_MS. Production runtime drives this directly
  // from the mic loop's matched label.
  useEffect(() => {
    if (liveLabelId !== undefined && liveLabelId !== null) {
      setActiveId(liveLabelId);
      return;
    }
    if (ids.length === 0) return;
    const t = window.setInterval(() => {
      const next = ids[Math.floor(Math.random() * ids.length)] ?? null;
      setActiveId(next);
      setPulse(true);
      window.setTimeout(() => setPulse(false), 250);
    }, SIM_TICK_MS);
    return () => window.clearInterval(t);
  }, [ids, liveLabelId]);

  // For wave layout we render N animated bars whose heights roll over
  // time; the active class drives the colour wash.
  const waveBars = useMemo(() => Array.from({ length: 18 }, (_, i) => i), []);
  const waveHeights = useWaveHeights(waveBars.length, !!activeId);

  const activeClass = classes.find((c) => c.id === activeId) ?? null;
  const activeCust = activeClass ? config.perClass[activeClass.id] : null;

  const themeStyle: React.CSSProperties = {
    '--bl-pub-bg': theme.bg,
    '--bl-pub-fg': theme.fg,
    '--bl-pub-accent': theme.accent,
    '--bl-pub-accent-fg': theme.accentFg,
    '--bl-pub-secondary': theme.secondary,
    '--bl-pub-surface': theme.surface,
    background: theme.gradient ?? theme.bg,
    color: theme.fg,
  } as React.CSSProperties;

  const resolvedOrder = resolveWidgetOrder(config.widgetOrder);

  return (
    <div
      className={`bl-pub-soundreactor${isWave ? ' bl-pub-soundreactor--wave' : ''}`}
      style={themeStyle}
      data-active-label={activeClass?.name ?? ''}
    >
      <header className="bl-pub-soundreactor__header">
        <h1 className="bl-pub-soundreactor__title">{config.text.title}</h1>
        {config.text.subtitle && (
          <p className="bl-pub-soundreactor__subtitle">{config.text.subtitle}</p>
        )}
      </header>

      <div className="bl-pub-soundreactor__stage" aria-live="polite">
        {!isWave && (
          <div
            className={`bl-pub-soundreactor__pulse${pulse ? ' bl-pub-soundreactor__pulse--hit' : ''}`}
            aria-hidden="true"
          />
        )}
        {isWave && (
          <div className="bl-pub-soundreactor__wave" aria-hidden="true">
            {waveBars.map((i) => (
              <span
                key={i}
                className="bl-pub-soundreactor__wave-bar"
                style={{ height: `${waveHeights[i]}%` }}
              />
            ))}
          </div>
        )}
        {activeCust && (
          <div className="bl-pub-soundreactor__hit-card">
            <span className="bl-pub-soundreactor__hit-emoji" aria-hidden="true">
              {activeCust.emoji ?? '🎵'}
            </span>
            <span className="bl-pub-soundreactor__hit-name">
              {activeCust.label ?? activeClass?.name}
            </span>
          </div>
        )}
      </div>

      <ul className="bl-pub-soundreactor__chips" aria-label="Class triggers">
        {classes.map((c) => {
          const cust = config.perClass[c.id];
          const isActive = c.id === activeId;
          return (
            <li
              key={c.id}
              className={`bl-pub-soundreactor__chip${isActive ? ' bl-pub-soundreactor__chip--active' : ''}`}
            >
              <span aria-hidden="true">{cust?.emoji ?? '🎵'}</span>
              <span>{cust?.label ?? c.name}</span>
            </li>
          );
        })}
      </ul>

      {/* Optional add-on widgets, in the kid's chosen order. We skip
          bigBanner (the hit card already plays that role on this
          template). */}
      {resolvedOrder.map((id) => {
        if (!config.widgets?.[id]) return null;
        if (id === 'bigBanner') return null;
        if (id === 'confidenceBars') {
          return <SoundConfidenceBars key={id} config={config} classes={classes} />;
        }
        if (id === 'historyStrip') {
          return null; // wave + hit card already convey "what just happened"
        }
        if (id === 'qrCode') {
          return (
            <div key={id} className="bl-pub-soundreactor__qr">
              <span className="bl-pub-soundreactor__qr-thumb" aria-hidden="true">
                <span>▣</span>
              </span>
              <span>Scan with a phone to try it</span>
            </div>
          );
        }
        if (id === 'shareLinks') {
          return (
            <div
              key={id}
              className="bl-pub-soundreactor__share"
              aria-label="Share buttons (preview)"
            >
              <p className="bl-pub-soundreactor__share-eyebrow">Show off</p>
              <div className="bl-pub-soundreactor__share-row">
                <span className="bl-pub-soundreactor__share-btn">
                  <span aria-hidden="true">🔗</span>
                  <span>Copy link</span>
                </span>
                <span className="bl-pub-soundreactor__share-btn">
                  <span aria-hidden="true">📤</span>
                  <span>Send</span>
                </span>
                <span className="bl-pub-soundreactor__share-btn">
                  <span aria-hidden="true">✉️</span>
                  <span>Email</span>
                </span>
              </div>
            </div>
          );
        }
        if (id === 'scoreStreak') {
          return null; // reactor template doesn't keep score (vibe page)
        }
        return null;
      })}

      {config.branding.showFooter && (
        <footer className="bl-pub-soundreactor__footer">
          Made on <strong>Bryta</strong>
        </footer>
      )}
    </div>
  );
}

/** Animated bar heights for the wave layout. Smooth random walk so
 *  the bars look alive even between confident matches. */
function useWaveHeights(count: number, _active: boolean): number[] {
  const [heights, setHeights] = useState<number[]>(() => Array.from({ length: count }, () => 30));
  const heightsRef = useRef(heights);
  heightsRef.current = heights;
  useEffect(() => {
    const id = window.setInterval(() => {
      setHeights(
        heightsRef.current.map((h) => {
          const next = h + (Math.random() - 0.5) * 35;
          return Math.max(10, Math.min(100, next));
        }),
      );
    }, 120);
    return () => window.clearInterval(id);
  }, []);
  return heights;
}

function SoundConfidenceBars({
  config,
  classes,
}: {
  config: PublishConfig;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
}): JSX.Element {
  const [probs, setProbs] = useState<number[]>(() =>
    Array(classes.length).fill(1 / Math.max(1, classes.length)),
  );
  useEffect(() => {
    if (classes.length === 0) return;
    const id = window.setInterval(() => {
      const next = Array.from({ length: classes.length }, () => Math.random());
      const s = next.reduce((a, b) => a + b, 0);
      setProbs(next.map((v) => v / s));
    }, 1500);
    return () => window.clearInterval(id);
  }, [classes.length]);
  return (
    <ul className="bl-pub-soundreactor__bars" aria-label="Confidence bars">
      {classes.map((c, i) => {
        const cust = config.perClass[c.id];
        const pct = Math.round((probs[i] ?? 0) * 100);
        return (
          <li key={c.id} className="bl-pub-soundreactor__bar">
            <span className="bl-pub-soundreactor__bar-name">
              {cust?.emoji ?? '🎵'} {cust?.label ?? c.name}
            </span>
            <span className="bl-pub-soundreactor__bar-track">
              <span className="bl-pub-soundreactor__bar-fill" style={{ width: `${pct}%` }} />
            </span>
            <span className="bl-pub-soundreactor__bar-pct">{pct}%</span>
          </li>
        );
      })}
    </ul>
  );
}
